import { useState } from 'react';
import { z } from 'zod';
import { DateTime, T, Var } from 'gt-react';
import { DownloadSimpleIcon, FileCsvIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

// Mirrors the harness export tools' result shape. Local (single consumer).
const resultSchema = z.object({
  fileName: z.string(),
  downloadUrl: z.url(),
  rowCount: z.number().int(),
  totalRows: z.number().int().optional(),
  truncated: z.boolean().optional(),
  expiresAt: z.string().optional(),
});

interface DownloadFileToolResultProps {
  /** The export tool's result (data lives in the result, not args). */
  result: unknown;
}

/**
 * Renders a harness export tool's result as a download card: the file name,
 * how many rows it holds, a capped warning, the link expiry, and a Download
 * button. Shared by the live chat and the history transcript, where an old
 * card shows as expired instead of a dead link.
 */
export function DownloadFileToolResult({
  result,
}: DownloadFileToolResultProps) {
  // Sampled once per mount: the card is either live or expired for as long
  // as it is on screen, and render stays pure.
  const [mountedAt] = useState(() => Date.now());
  const parsed = resultSchema.safeParse(result);
  if (!parsed.success) return null;
  const file = parsed.data;
  const expiresAt = file.expiresAt ? new Date(file.expiresAt) : null;
  const expired = expiresAt !== null && expiresAt.getTime() < mountedAt;

  return (
    <Card className="flex flex-col gap-2 p-3">
      <div className="flex items-center gap-2">
        <FileCsvIcon className="text-muted-foreground h-5 w-5 shrink-0" />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-card-foreground truncate text-sm font-medium">
            {file.fileName}
          </span>
          <span className="text-muted-foreground text-xs">
            <T context="row count on a chat download card">
              <Var>{file.rowCount}</Var> rows
            </T>
            {file.truncated && file.totalRows !== undefined ? (
              <>
                {' · '}
                <T context="warning that a chat export hit the row cap">
                  capped — <Var>{file.totalRows}</Var> matched
                </T>
              </>
            ) : null}
          </span>
        </div>
        {expired ? (
          <Button size="sm" variant="outline" disabled>
            <T context="download button when the link has expired">
              Link expired
            </T>
          </Button>
        ) : (
          <Button size="sm" asChild>
            <a
              href={file.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              download={file.fileName}
            >
              <DownloadSimpleIcon className="h-4 w-4" />
              <T context="download button on a chat export card">
                Download CSV
              </T>
            </a>
          </Button>
        )}
      </div>
      {expiresAt && !expired ? (
        <span className="text-muted-foreground text-xs">
          <T context="expiry note on a chat download card">
            Link expires <DateTime>{expiresAt}</DateTime>.
          </T>
        </span>
      ) : null}
    </Card>
  );
}
